import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ArticleCard from "@/components/feature/ArticleCard";
import useSeo from "@/hooks/useSeo";
import { articles } from "@/mocks/articles";
import { categories } from "@/mocks/categories";
import { siteInfo } from "@/mocks/site";

export default function Topics() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCat = searchParams.get("cat") ?? "all";
  const [keyword, setKeyword] = useState(searchParams.get("q") ?? "");

  const activeCatName = categories.find((c) => c.id === activeCat)?.name ?? "全部内容";
  const activeCatDesc =
    categories.find((c) => c.id === activeCat)?.desc ??
    "汇总全部热点整理，按发布时间从新到旧排列，可按主题筛选或关键词检索。";

  const filtered = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    return articles.filter((article) => {
      const matchCat = activeCat === "all" || article.categoryId === activeCat;
      const matchKw =
        !kw ||
        article.title.toLowerCase().includes(kw) ||
        article.summary.toLowerCase().includes(kw) ||
        article.tags.some((tag) => tag.toLowerCase().includes(kw));
      return matchCat && matchKw;
    });
  }, [activeCat, keyword]);

  useSeo({
    title: `${activeCatName} - 热点聚合 - 17爆料网`,
    description: `${activeCatDesc} 共 ${filtered.length} 篇热点整理，支持按主题筛选与关键词检索。`,
    keywords: "热点聚合,爆料列表,事件整理,17爆料网",
    path: "/topics",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: `${activeCatName} - 17爆料网`,
        description: activeCatDesc,
        inLanguage: "zh-CN",
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "https://17baoliao.example.com/" },
          { "@type": "ListItem", position: 2, name: "热点聚合", item: "https://17baoliao.example.com/topics" },
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "ItemList",
        itemListElement: filtered.map((article, index) => ({
          "@type": "ListItem",
          position: index + 1,
          name: article.title,
          url: `https://17baoliao.example.com/article/${article.id}`,
        })),
      },
    ],
  });

  const selectCategory = (id: string) => {
    const next = new URLSearchParams(searchParams);
    if (id === "all") {
      next.delete("cat");
    } else {
      next.set("cat", id);
    }
    setSearchParams(next);
  };

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-50">
      <SiteHeader />
      <main className="w-full flex-1">
        <div className="w-full px-4 pt-6 md:px-8">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "热点聚合" }]} />
        </div>

        <section className="w-full px-4 pb-6 pt-5 md:px-8 md:pb-8">
          <h1 className="font-heading text-2xl font-black tracking-tight text-foreground-950 md:text-3xl">
            {activeCatName}
          </h1>
          <p className="mt-3 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
            {activeCatDesc}
          </p>
        </section>

        <section className="sticky top-16 z-30 border-y border-background-200 bg-background-50/95 backdrop-blur">
          <div className="w-full px-4 py-4 md:px-8">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => selectCategory("all")}
                  className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors cursor-pointer ${
                    activeCat === "all"
                      ? "bg-primary-500 font-medium text-background-50"
                      : "bg-background-100 text-foreground-700 hover:bg-background-200"
                  }`}
                >
                  全部
                </button>
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    type="button"
                    onClick={() => selectCategory(cat.id)}
                    className={`flex items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors cursor-pointer ${
                      activeCat === cat.id
                        ? "bg-primary-500 font-medium text-background-50"
                        : "bg-background-100 text-foreground-700 hover:bg-background-200"
                    }`}
                  >
                    <i className={`${cat.icon} text-base`}></i>
                    {cat.name}
                  </button>
                ))}
              </div>

              <div className="relative w-full lg:w-72">
                <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base text-foreground-400"></i>
                <input
                  type="search"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="搜索标题、摘要或标签"
                  className="w-full rounded-md border border-background-300 bg-background-50 py-2.5 pl-9 pr-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="w-full px-4 py-8 md:px-8 md:py-10">
          <p className="text-sm text-foreground-500">
            共找到 <strong className="font-bold text-foreground-900">{filtered.length}</strong> 篇整理
          </p>

          {filtered.length > 0 ? (
            <div className="mt-4">
              {filtered.map((article) => (
                <ArticleCard key={article.id} article={article} variant="wide" />
              ))}
            </div>
          ) : (
            <div className="mt-6 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-16 text-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-500">
                <i className="ri-file-search-line text-2xl"></i>
              </span>
              <h2 className="mt-4 font-heading text-lg font-bold text-foreground-900">没有匹配的内容</h2>
              <p className="mt-2 max-w-md text-sm text-foreground-600">
                换个关键词，或者切换到其他主题分类看看。也可以直接通过联系页面告诉我们你想了解的事件。
              </p>
              <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setKeyword("");
                    selectCategory("all");
                  }}
                  className="whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
                >
                  重置筛选
                </button>
                <Link
                  to="/contact"
                  className="whitespace-nowrap rounded-md border border-foreground-300 px-5 py-2.5 text-sm text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-600 cursor-pointer"
                >
                  告知我们
                </Link>
              </div>
            </div>
          )}
        </section>

        <section className="w-full px-4 pb-14 md:px-8">
          <div className="flex flex-col items-start gap-4 rounded-lg border border-background-200 bg-background-100 p-6 md:flex-row md:items-center md:justify-between">
            <div>
              <h2 className="font-heading text-base font-bold text-foreground-950">没找到想看的分类？</h2>
              <p className="mt-1.5 text-sm text-foreground-600">
                我们会根据读者反馈持续补充主题方向，欢迎告诉我们你关注的事件类型。
              </p>
            </div>
            <Link
              to="/contact"
              className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md bg-secondary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-secondary-600 cursor-pointer"
            >
              <i className="ri-chat-3-line text-base"></i>
              提出建议
            </Link>
          </div>
          <p className="mt-6 text-xs leading-relaxed text-foreground-500">
            说明：{siteInfo.name} 的内容均基于公开信息整理，如发现信息有误，欢迎通过联系页面提交更正材料。
          </p>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}